import React, { useCallback } from 'react';

import WidgetSettingsShowDataForEditor from 'widgets/WidgetSettingsEditing/WidgetSettingsShowDataForEditor';
import WidgetNewportAppSettingsEditor from 'widgets/WidgetSettingsEditing/WidgetNewportAppSettingsEditor';
import { useWidgetConfigUpdater } from 'widgets/WidgetSettingsEditing/useWidgetConfigUpdater';

import { CapsuleSettingsSymbolEditor } from 'widgets/tiles/components/tile/CapsuleSettingsSymbolEditor';
import { PlCapsuleConfigState } from 'widgets/plCapsule/store/config/PlCapsuleConfigState';
import {
    useDispatchUpdatePlCapsuleConfiguration,
    usePlCapsuleConfigSlice,
    useSelectPlCapsuleConfig,
} from 'widgets/plCapsule/store/config/configslice';
import './PlCapsuleSettings.scss';
import { PLBenchmarkDropdownProps } from 'widgets/fillsGraph/pl/PLBenchmarkDropdown';
import {
    Dropdown,
    DropdownItemProps,
} from '@instinet-analytics/semantic-ui-components';
import {
    earliestArrivalLabel,
    individualArrivalLabel,
    lastLabel,
    PnlBenchmark,
} from 'utils/PnL';
import { xVwapLabel } from 'widgets/fillsGraph/legend/labels';
import WidgetSettingsCheckboxValueEditor from '../../../WidgetSettingsEditing/WidgetSettingsCheckboxValueEditor';

export const PlCapsuleSettingsText = `P&L Capsule Settings`;

export interface PlSettingsProps {
    config?: PlCapsuleConfigState;
    onChange?: (nextDelta: Partial<PlCapsuleConfigState>) => void;
}

export default function PlCapsuleSettings(props: PlSettingsProps) {
    const { onChange, config: propsConfig } = props;
    const { config: configSliceConfig, dispatchConfigChanged } =
        usePlCapsuleConfigSlice();
    const { debouncedApplyWidgetConfigChange } = useWidgetConfigUpdater(
        dispatchConfigChanged,
        onChange
    );
    const config = propsConfig ?? configSliceConfig;

    return (
        <div className="PlCapsuleSettings">
            <div className="group ">
                <CapsuleSettingsSymbolEditor
                    config={config}
                    onChange={debouncedApplyWidgetConfigChange}
                />

                <WidgetSettingsCheckboxValueEditor
                    propertyName={'hideRealized'}
                    config={config}
                    label={'Realized'}
                    inverted={true}
                    onChange={dispatchConfigChanged}
                />

                <WidgetSettingsCheckboxValueEditor
                    propertyName={'showUnrealized'}
                    config={config}
                    label={'Unrealized'}
                    onChange={dispatchConfigChanged}
                />

                <WidgetSettingsCheckboxValueEditor
                    propertyName={'showTotal'}
                    config={config}
                    label={'Total'}
                    onChange={dispatchConfigChanged}
                />

                <WidgetSettingsShowDataForEditor
                    config={config}
                    onChange={debouncedApplyWidgetConfigChange}
                />
            </div>

            <div>
                <div>Benchmark</div>
                <PLBenchmarkDropdown />
            </div>

            <WidgetNewportAppSettingsEditor />
        </div>
    );
}

export function PLBenchmarkDropdown(props: PLBenchmarkDropdownProps) {
    const { inlineToolbar } = props;
    const pnlConfig = useSelectPlCapsuleConfig();
    const updateConfig = useDispatchUpdatePlCapsuleConfiguration();

    const pnlBenchmarkDropdownValues: DropdownItemProps[] = [
        {
            text: individualArrivalLabel,
            value: PnlBenchmark.IndividualOrderArrival,
        },
        {
            text: earliestArrivalLabel,
            value: PnlBenchmark.Arrival,
        },
        {
            text: xVwapLabel,
            value: PnlBenchmark.Vwap,
        },
        {
            text: lastLabel,
            value: PnlBenchmark.Last,
        },
    ];

    const onChange = useCallback(
        (pnlChartBenchmark: PnlBenchmark) => {
            updateConfig({
                pnlChartBenchmark,
            });
        },
        [updateConfig]
    );

    return (
        <Dropdown
            className="PLBenchmarkDropdown"
            options={pnlBenchmarkDropdownValues}
            value={pnlConfig.pnlChartBenchmark}
            onChange={onChange}
            inlineToolbar={inlineToolbar}
        />
    );
}
